// Plays the programme video flow straight from TAMS, one presigned segment
// at a time.
//
// Every programme segment is self-contained (the track's init.mp4 followed
// by the CMAF fragment — processing-contracts.md §1.3), so a plain <video>
// can play each object alone; the player chains to the next segment on
// `ended`. Only the video flow is played: audio is a separate TAMS flow and
// muxing it would need MSE, which this preview does not attempt.
//
// Media time inside a segment starts at its tfdt, not zero, so the player
// maps through the element's first buffered/seekable time (`base`) rather
// than assuming currentTime == offset into the segment.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, VideoOff } from 'lucide-react';
import { useTamsSegments } from '@/hooks/useProcessing';
import { formatTimerange, parseTimerange } from '@/lib/timerange';

/** Segments fetched per window. */
const WINDOW_S = 300;

interface ParsedSegment {
  start: number;
  end: number;
  url: string;
}

export interface SegmentPlayerProps {
  videoFlowId: string | undefined;
  /** Where playback should be; changing it seeks. Epoch seconds. */
  seekTo: number;
  /** Bumped by the parent to force a seek to the same `seekTo`. */
  seekNonce: number;
  onTime: (t: number) => void;
}

export default function SegmentPlayer({ videoFlowId, seekTo, seekNonce, onTime }: SegmentPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [windowStart, setWindowStart] = useState(seekTo);
  const [index, setIndex] = useState(0);
  const pendingSeek = useRef<number | null>(seekTo);
  const base = useRef(0);
  // True while the viewer is playing, so the next segment (or window)
  // autoplays across the object boundary.
  const [continuing, setContinuing] = useState(false);

  const timerange = formatTimerange(windowStart, windowStart + WINDOW_S);
  const { data: rawSegments, isLoading, isError } = useTamsSegments(videoFlowId, { timerange, limit: 200 });

  const segments = useMemo<ParsedSegment[]>(() => {
    const out: ParsedSegment[] = [];
    for (const s of rawSegments ?? []) {
      const r = parseTimerange(s.timerange);
      const url = s.get_urls?.[0]?.url;
      if (r?.start == null || r.end == null || !url) continue;
      out.push({ start: r.start, end: r.end, url });
    }
    return out.sort((a, b) => a.start - b.start);
  }, [rawSegments]);

  // Parent seek: move the window and pick the covering segment.
  useEffect(() => {
    pendingSeek.current = seekTo;
    const hit = segments.findIndex((s) => seekTo >= s.start && seekTo < s.end);
    if (hit >= 0) {
      setIndex(hit);
      const v = videoRef.current;
      if (v && hit === index && v.readyState > 0) {
        v.currentTime = base.current + (seekTo - segments[hit].start);
        pendingSeek.current = null;
      }
    } else {
      setWindowStart(seekTo);
      setIndex(0);
    }
    // Deliberately keyed on the seek request only, not on segments/index.
  }, [seekTo, seekNonce]);

  // A fresh window: place the pending seek.
  useEffect(() => {
    if (pendingSeek.current == null || segments.length === 0) return;
    const t = pendingSeek.current;
    const hit = segments.findIndex((s) => t >= s.start && t < s.end);
    setIndex(hit >= 0 ? hit : 0);
  }, [segments]);

  const current = segments[index];

  const onLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v || !current) return;
    base.current = v.seekable.length > 0 ? v.seekable.start(0) : 0;
    if (pendingSeek.current != null) {
      const offset = pendingSeek.current - current.start;
      if (offset > 0 && offset < current.end - current.start) v.currentTime = base.current + offset;
      pendingSeek.current = null;
    }
  };

  const onEnded = () => {
    if (index + 1 < segments.length) {
      setContinuing(true);
      setIndex(index + 1);
    } else if (current) {
      setContinuing(true);
      // Past the fetched window: fetch the next one from where we are.
      pendingSeek.current = current.end;
      setWindowStart(current.end);
    }
  };

  if (!videoFlowId) {
    return (
      <div className="aspect-video flex flex-col items-center justify-center bg-jeket-black-deep rounded-lg text-jeket-silver-muted text-sm gap-2">
        <VideoOff size={28} className="opacity-50" />
        No programme video retained for this recording
      </div>
    );
  }

  return (
    <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
      {current ? (
        <video
          ref={videoRef}
          key={current.url}
          src={current.url}
          controls
          muted
          playsInline
          autoPlay={continuing}
          onPlay={() => setContinuing(true)}
          onPause={(e) => {
            if (!e.currentTarget.ended) setContinuing(false);
          }}
          onLoadedMetadata={onLoadedMetadata}
          onTimeUpdate={() => {
            const v = videoRef.current;
            if (v && current) onTime(current.start + (v.currentTime - base.current));
          }}
          onEnded={onEnded}
          className="w-full h-full"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-jeket-silver-muted text-sm gap-2">
          {isLoading ? (
            <>
              <Loader2 size={18} className="animate-spin text-jeket-gold" /> Loading programme…
            </>
          ) : isError ? (
            <span className="text-jeket-error">Couldn't load programme segments</span>
          ) : (
            'No programme media at this point'
          )}
        </div>
      )}
      <span className="absolute top-2 left-2 px-1.5 py-0.5 text-[10px] rounded bg-black/70 text-jeket-silver-muted">
        Video preview · audio not played
      </span>
    </div>
  );
}
